/**
 * Per-channel access control — a unified data layer over the two backend
 * access models the messaging platform ships:
 *
 *  - PHONE channels → NumberAccess grants   (`/api/messaging/access`),
 *    permission tokens READ / SEND / ADMIN.
 *  - EMAIL channels → mailbox shares         (`/messaging/email/accounts/:id/shares`),
 *    permission tokens EMAIL_READ / EMAIL_WRITE.
 *
 * The component that consumes these hooks stays channel-agnostic: every hook
 * branches internally on `channel.kind` and normalises both backends onto one
 * grant shape with a friendly `level` of 'view' | 'reply'.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiDelete, apiGet, apiPost, operationsClient } from '@/lib/api';
import { messagingKeys } from '@/lib/queryKeys';
import { emailAccountsKey } from '@/hooks/email/useEmailAccounts';
import type { Channel } from './types';

// ─── Unified grant model ────────────────────────────────────────────────────

/** "Can view" vs "Can view & reply" — hides the phone/email token split. */
export type ChannelAccessLevel = 'view' | 'reply';

export type ChannelPrincipalType = 'USER' | 'GROUP' | 'ROLE' | 'JOB_SCOPED';

/** What the add-access picker offers. JOB_SCOPED is derived from the schedule
 * and never granted by hand. */
export type GrantablePrincipalType = 'USER' | 'GROUP' | 'ROLE';

/**
 * What the access hooks need to know about a channel. A full {@link Channel}
 * satisfies it; so does a just-connected mailbox that has no inbox row yet —
 * the sharing step right after connect targets the mailbox directly.
 */
export type ChannelAccessTarget = Pick<
  Channel,
  'id' | 'kind' | 'channelAccountId' | 'phoneNumberId'
>;

export interface ChannelGrant {
  id: string;
  principalType: ChannelPrincipalType;
  principalId: string;
  /** Raw backend tokens, kept for debugging / forward-compat. */
  permissions: string[];
  level: ChannelAccessLevel;
}

export interface GrantChannelAccessInput {
  principalType: GrantablePrincipalType;
  principalId: string;
  level: ChannelAccessLevel;
}

// ─── Raw backend shapes ─────────────────────────────────────────────────────

interface RawGrant {
  id: string;
  principalType: ChannelPrincipalType;
  principalId: string;
  permissions: string[];
}

/** The number-access list is wrapped `{ data }` server-side, but tolerate a
 * bare array or an `{ items }` envelope so a backend tweak can't blank the UI. */
function unwrapList(payload: unknown): RawGrant[] {
  if (Array.isArray(payload)) return payload as RawGrant[];
  if (payload && typeof payload === 'object') {
    const obj = payload as Record<string, unknown>;
    if (Array.isArray(obj.data)) return obj.data as RawGrant[];
    if (Array.isArray(obj.items)) return obj.items as RawGrant[];
    if (Array.isArray(obj.shares)) return obj.shares as RawGrant[];
  }
  return [];
}

// ─── Token ⇄ level mapping ──────────────────────────────────────────────────

function phoneLevelOf(permissions: string[]): ChannelAccessLevel {
  return permissions.includes('SEND') || permissions.includes('ADMIN') ? 'reply' : 'view';
}
function emailLevelOf(permissions: string[]): ChannelAccessLevel {
  return permissions.includes('EMAIL_WRITE') ? 'reply' : 'view';
}
function phonePermissions(level: ChannelAccessLevel): string[] {
  return level === 'reply' ? ['READ', 'SEND'] : ['READ'];
}
function emailPermissions(level: ChannelAccessLevel): string[] {
  return level === 'reply' ? ['EMAIL_READ', 'EMAIL_WRITE'] : ['EMAIL_READ'];
}

function normalize(raw: RawGrant, kind: Channel['kind']): ChannelGrant {
  return {
    id: raw.id,
    principalType: raw.principalType,
    principalId: raw.principalId,
    permissions: raw.permissions ?? [],
    level:
      kind === 'EMAIL' ? emailLevelOf(raw.permissions ?? []) : phoneLevelOf(raw.permissions ?? []),
  };
}

// ─── URL builders ───────────────────────────────────────────────────────────

const numberAccessUrl = {
  list: () => '/api/messaging/access',
  grant: () => '/api/messaging/access',
  revoke: (grantId: string) => `/api/messaging/access/${grantId}`,
};
const mailboxSharesUrl = {
  list: (channelAccountId: string) => `/messaging/email/accounts/${channelAccountId}/shares`,
  share: (channelAccountId: string) => `/messaging/email/accounts/${channelAccountId}/shares`,
  revoke: (channelAccountId: string, grantId: string) =>
    `/messaging/email/accounts/${channelAccountId}/shares/${grantId}`,
};

/**
 * Strip the dual-store `:N` id suffix (e.g. `…a501:0`) so the raw UUID reaches
 * the Postgres-native access endpoints, which validate `@IsUuid`/`ParseUuidPipe`
 * and reject the suffixed form. A no-op for already-clean ids.
 */
function rawResourceId(id: string | null | undefined): string | undefined {
  return id ? id.replace(/:\d+$/, '') : undefined;
}

/**
 * A share change can change what the CURRENT user can see — an admin sharing a
 * personal mailbox with themselves is how they open it — so refresh everything
 * visibility-scoped, not just the grant list: the channel rail + threads (all
 * under `messagingKeys.all()`) and, for a mailbox, the email accounts list the
 * composer's mailbox picker reads.
 */
function invalidateAfterAccessChange(
  qc: ReturnType<typeof useQueryClient>,
  channel: ChannelAccessTarget,
): void {
  // `all()` covers the grant list, channels and threads alike.
  void qc.invalidateQueries({ queryKey: messagingKeys.all() });
  if (channel.kind === 'EMAIL') {
    void qc.invalidateQueries({ queryKey: emailAccountsKey });
  }
}

// ─── Hooks ──────────────────────────────────────────────────────────────────

/** List the access grants for a channel (its NumberAccess or mailbox shares),
 * normalised to the unified {@link ChannelGrant} shape. */
export function useChannelAccess(channel: ChannelAccessTarget) {
  const resourceId = rawResourceId(
    channel.kind === 'EMAIL' ? channel.channelAccountId : channel.phoneNumberId,
  );
  return useQuery({
    queryKey: messagingKeys.channelAccess(channel.id),
    enabled: !!resourceId,
    staleTime: 30_000,
    queryFn: async (): Promise<ChannelGrant[]> => {
      if (!resourceId) return [];
      const raw =
        channel.kind === 'EMAIL'
          ? await apiGet<unknown>(operationsClient, mailboxSharesUrl.list(resourceId))
          : await apiGet<unknown>(operationsClient, numberAccessUrl.list(), {
              params: { phoneNumberId: resourceId },
            });
      return unwrapList(raw).map((g) => normalize(g, channel.kind));
    },
  });
}

/** Grant a principal "view" or "view & reply" on a channel. */
export function useGrantChannelAccess(channel: ChannelAccessTarget) {
  const qc = useQueryClient();
  const resourceId = rawResourceId(
    channel.kind === 'EMAIL' ? channel.channelAccountId : channel.phoneNumberId,
  );
  return useMutation({
    mutationFn: (input: GrantChannelAccessInput) => {
      if (!resourceId) {
        return Promise.reject(new Error('Channel is not linked to a number or mailbox'));
      }
      if (channel.kind === 'EMAIL') {
        return apiPost(operationsClient, mailboxSharesUrl.share(resourceId), {
          principalType: input.principalType,
          principalId: input.principalId,
          permissions: emailPermissions(input.level),
        });
      }
      return apiPost(operationsClient, numberAccessUrl.grant(), {
        phoneNumberId: resourceId,
        principalType: input.principalType,
        principalId: input.principalId,
        permissions: phonePermissions(input.level),
      });
    },
    onSuccess: () => invalidateAfterAccessChange(qc, channel),
  });
}

/** Revoke a single grant (by its id) from a channel. */
export function useRevokeChannelAccess(channel: ChannelAccessTarget) {
  const qc = useQueryClient();
  const resourceId = rawResourceId(
    channel.kind === 'EMAIL' ? channel.channelAccountId : channel.phoneNumberId,
  );
  return useMutation({
    mutationFn: (grantId: string) => {
      if (channel.kind === 'EMAIL') {
        if (!resourceId) {
          return Promise.reject(new Error('Channel is not linked to a mailbox'));
        }
        return apiDelete(operationsClient, mailboxSharesUrl.revoke(resourceId, grantId));
      }
      return apiDelete(operationsClient, numberAccessUrl.revoke(grantId));
    },
    onSuccess: () => invalidateAfterAccessChange(qc, channel),
  });
}
